import { Link } from "@tanstack/react-router";
import { useMe } from "../../lib/me";
import { ErrorText, Section } from "../../ui";
import { TodayMeetings } from "../meet/TodayMeetings";
import { dueState, dueTodayForMe, formatDue, type Task } from "../tasks/model";
import { Badge } from "../tasks/TaskRow";
import { useTaskActions } from "../tasks/use-task-actions";
import type { TasksState } from "../tasks/use-tasks";

/** Today: my open tasks due today or overdue (live, from the tasks listeners), then meetings. */
export function TodayPanel({ workspaceId, tasks, now }: { workspaceId: string; tasks: TasksState; now: Date }) {
  const me = useMe();
  const actions = useTaskActions();
  const due: Task[] = tasks.status === "ready" ? dueTodayForMe(tasks.tasks, me.uid, now) : [];

  return (
    <Section title="Today">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">My tasks due</h3>
          {tasks.status === "loading" ? <p className="text-sm text-muted">Loading tasks…</p> : null}
          {tasks.status === "error" ? <ErrorText>Tasks could not be loaded: {tasks.message}</ErrorText> : null}
          {tasks.status === "ready" && due.length === 0 ? (
            <p className="text-sm text-muted">Nothing due today.</p>
          ) : null}
          {due.length > 0 ? (
            <ul aria-label="My tasks due today" className="flex flex-col gap-1">
              {due.map((t) => (
                <li key={t.id} className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => actions.toggle(workspaceId, t.id, true)}
                    aria-label={`Mark “${t.title}” done`}
                    className="size-5 shrink-0 accent-[var(--accent)]"
                  />
                  <span className="min-w-0 flex-1 truncate text-text">{t.title}</span>
                  {t.due === null ? null : dueState(t.due, now) === "overdue" ? (
                    <Badge className="border-danger text-danger">{`Overdue: ${formatDue(t.due, now)}`}</Badge>
                  ) : (
                    <Badge className="border-accent text-accent">Today</Badge>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          <Link to="/w/$workspaceId/tasks" params={{ workspaceId }} className="text-sm text-accent underline-offset-2 hover:underline">
            All tasks
          </Link>
        </div>
        <div className="flex flex-col gap-2 border-t border-border pt-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">Meetings</h3>
          <TodayMeetings workspaceId={workspaceId} now={now} />
        </div>
      </div>
    </Section>
  );
}
